import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { modulesAPI, progressAPI, assessmentAPI, templateAPI, timeTrackAPI } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ContentRenderer from "@/components/ContentRenderer";
import { toast } from "@/components/ui/sonner";
import {
  BookOpen,
  FileText,
  CheckSquare,
  ClipboardList,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  ArrowRight,
  Brain,
  Loader2,
  Headphones,
  Clock,
  Home,
  ExternalLink,
} from "lucide-react";

// NotebookLM Audio URLs for each module
const NOTEBOOK_LM_URLS = {
  1: "https://notebooklm.google.com/notebook/07695de2-7efa-4c8c-a762-b2be22072f99",
  2: "https://notebooklm.google.com/notebook/143e1afd-c1f4-4c44-9ed4-433dc1ba4f25",
  3: "https://notebooklm.google.com/notebook/7fb4b643-4ab7-4b5f-b20d-cd95f3a3c14f",
  4: "https://notebooklm.google.com/notebook/892991f9-b70a-4b6a-9a2b-a7cb7fcf77f9",
  5: "https://notebooklm.google.com/notebook/cbf1c16f-b923-49a2-8723-578a12a285b6",
  6: "https://notebooklm.google.com/notebook/200b4f17-6f21-43f1-95db-3d9d494cf0a8",
  7: "https://notebooklm.google.com/notebook/97b4ce16-a8b1-4213-a664-edc0db25d668",
  8: "https://notebooklm.google.com/notebook/eb6b8920-4b0f-46ab-a868-bd94b24d2853",
  9: "https://notebooklm.google.com/notebook/636595cc-8918-4cd1-8545-5fb00f3f9ce2",
  10: "https://notebooklm.google.com/notebook/fc372335-598d-49a2-b78e-f526ff6b8d40",
  11: "https://notebooklm.google.com/notebook/be84c104-b38d-40d5-bf5c-882110533a2e",
  12: "https://notebooklm.google.com/notebook/52f6856b-8add-4e5b-9735-5fef29162ae3",
  13: "https://notebooklm.google.com/notebook/48a64bbc-837b-49e5-9fd5-2af91b2464aa",
  14: "https://notebooklm.google.com/notebook/05af9c80-2856-4f82-a6ad-f14d7129a4be",
  15: "https://notebooklm.google.com/notebook/0e8ce841-37bb-4da0-8bc3-9328288a1958",
  16: "https://notebooklm.google.com/notebook/5153fd9b-64de-4e5e-a56b-b50a23b4fe32",
  17: "https://notebooklm.google.com/notebook/331609c3-7b94-4703-8cde-5e7b56ba92d4",
  18: "https://notebooklm.google.com/notebook/b795cd6c-d9a0-4599-a811-c50c77144f61",
  19: "https://notebooklm.google.com/notebook/b0d4921d-5507-49cd-a3e9-5fb27b09b012",
  20: "https://notebooklm.google.com/notebook/ac338dfa-e001-4d46-b74f-4d289a057654",
};

// Estimated reading times per module (in minutes)
const READING_TIMES = {
  1: 12, 2: 15, 3: 14, 4: 16, 5: 13, 6: 15, 7: 12, 8: 14, 9: 13, 10: 11,
  11: 12, 12: 14, 13: 13, 14: 11, 15: 10, 16: 14, 17: 12, 18: 13, 19: 11, 20: 14,
  21: 18, 22: 14
};

export default function ModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("learning");
  const [activeLearningSection, setActiveLearningSection] = useState(0);

  // Assessment state
  const [answers, setAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [gradingMode, setGradingMode] = useState("standard"); // "standard" or "ai"

  // Checklist state
  const [checkedItems, setCheckedItems] = useState({});

  // Time tracking
  const timeRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Start time tracking
    timeRef.current = 0;
    intervalRef.current = setInterval(() => {
      timeRef.current += 30;
      if (timeRef.current % 30 === 0 && moduleId) {
        timeTrackAPI.track(parseInt(moduleId), 30).catch(() => {});
      }
    }, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Send final time on unmount
      if (timeRef.current > 0 && moduleId) {
        timeTrackAPI.track(parseInt(moduleId), timeRef.current % 30).catch(() => {});
      }
    };
  }, [moduleId]);

  const loadModule = useCallback(async () => {
    try {
      const res = await modulesAPI.getOne(parseInt(moduleId));
      setModule(res.data);
      setActiveLearningSection(0);
      setAnswers({});
      setAssessmentResult(null);
      setCheckedItems({});
    } catch (err) {
      console.error("Failed to load module:", err);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    setLoading(true);
    loadModule();
  }, [loadModule]);

  const markSectionComplete = async (section) => {
    const labels = {
      learning: "Learning",
      sops: "SOPs & Templates",
      checklists: "Checklists",
    };
    try {
      await progressAPI.update(parseInt(moduleId), section);
      toast.success(`${labels[section] || "Section"} marked complete`, {
        description: "Your dashboard progress has been updated.",
      });
      await loadModule();
    } catch (err) {
      console.error("Failed to update progress:", err);
      toast.error("Could not update progress. Please try again.");
    }
  };

  const handleAssessmentSubmit = async (mode) => {
    setSubmitting(true);
    try {
      const res = mode === "ai"
        ? await assessmentAPI.aiGrade(parseInt(moduleId), answers)
        : await assessmentAPI.submit(parseInt(moduleId), answers);
      setAssessmentResult(res.data);
      // Don't reload immediately - show results first
      // loadModule will be called when user navigates away or retakes
    } catch (err) {
      console.error("Failed to submit assessment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-400 text-sm uppercase tracking-wider">Loading module...</div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Module not found.</p>
        <Button onClick={() => navigate("/")} variant="ghost" className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const learningSections = module.learning?.sections || [];
  const sops = module.sops || [];
  const checklists = module.checklists || [];
  const assessment = module.assessment || {};
  const questions = assessment.questions || [];
  const progress = module.progress || {};

  const modId = parseInt(moduleId);
  const hasPrev = modId > 1;
  const hasNext = modId < 22; // Updated to support 22 modules

  const sectionProgress = ["learning", "sops", "checklists", "assessment"].filter(
    (s) => progress[s]
  ).length;
  const completionPct = Math.round((sectionProgress / 4) * 100);
  
  const notebookUrl = NOTEBOOK_LM_URLS[modId];
  const readingTime = READING_TIMES[modId] || 12;

  return (
    <div className="space-y-6" data-testid="module-page">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb" data-testid="breadcrumb">
        <Link 
          to="/" 
          className="flex items-center gap-1 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Home</span>
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-500">Modules</span>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-900 font-medium truncate max-w-[200px]">{module.title}</span>
      </nav>

      {/* Sticky Progress Bar */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" data-testid="sticky-progress">
        <div className="flex items-center justify-between gap-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Module {moduleId}</span>
            <div className="hidden sm:flex items-center gap-2">
              <Progress value={completionPct} className="w-32 h-1.5" />
              <span className="text-xs text-slate-500">{completionPct}% complete</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              <span>~{readingTime} min read</span>
            </div>
            {completionPct === 100 && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                Complete
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className="text-xs tracking-wider uppercase">
              Module {moduleId}
            </Badge>
            {completionPct === 100 && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                Complete
              </Badge>
            )}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {module.title}
          </h1>
          <p className="text-slate-500 mt-1 text-sm font-body">{module.description}</p>
        </div>
      </div>

      {/* Audio Player Card - Link to NotebookLM */}
      {notebookUrl && (
        <a
          href={notebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gradient-to-r from-slate-900 to-slate-800 rounded-sm p-4 shadow-sm hover:shadow-md transition-all group"
          data-testid="audio-player-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#005d6c] rounded-full flex items-center justify-center group-hover:scale-105 transition-transform">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-white font-medium text-sm">Listen to this module as a podcast</div>
              <div className="text-slate-400 text-xs mt-0.5">Opens in Google NotebookLM • ~15 min</div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
          </div>
        </a>
      )}

      <Separator />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 rounded-sm h-10 p-1">
          <TabsTrigger
            value="learning"
            data-testid="tab-learning"
            className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Learning</span>
          </TabsTrigger>
          <TabsTrigger
            value="sops"
            data-testid="tab-sops"
            className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">SOPs & Templates</span>
          </TabsTrigger>
          <TabsTrigger
            value="checklists"
            data-testid="tab-checklists"
            className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2"
          >
            <CheckSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Checklists</span>
          </TabsTrigger>
          <TabsTrigger
            value="assessment"
            data-testid="tab-assessment"
            className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Assessment</span>
          </TabsTrigger>
        </TabsList>

        {/* Learning Tab */}
        <TabsContent value="learning" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Section Nav */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-slate-200 rounded-sm p-3 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold px-2 py-1">
                  Sections
                </div>
                {learningSections.map((section, idx) => (
                  <button
                    key={idx}
                    data-testid={`learning-section-${idx}`}
                    onClick={() => setActiveLearningSection(idx)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-all duration-200 ${
                      activeLearningSection === idx
                        ? "bg-slate-900 text-white font-medium"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
              {!progress.learning && (
                <Button
                  data-testid="mark-learning-complete"
                  onClick={() => markSectionComplete("learning")}
                  className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm"
                >
                  <Check className="w-3.5 h-3.5 mr-2" />
                  Mark as Complete
                </Button>
              )}
              {progress.learning && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-700 text-sm">
                  <Check className="w-3.5 h-3.5" />
                  Completed
                </div>
              )}
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
                {learningSections[activeLearningSection] && (
                  <>
                    <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                      {learningSections[activeLearningSection].title}
                    </h2>
                    <ContentRenderer
                      content={learningSections[activeLearningSection].content}
                    />
                  </>
                )}
                {/* Section navigation */}
                <div className="flex items-center justify-between mt-8 pt-4 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    disabled={activeLearningSection === 0}
                    onClick={() => setActiveLearningSection((p) => p - 1)}
                    className="text-sm text-slate-600"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                    Previous
                  </Button>
                  <span className="text-xs text-slate-400">
                    {activeLearningSection + 1} of {learningSections.length}
                  </span>
                  <Button
                    variant="ghost"
                    disabled={activeLearningSection === learningSections.length - 1}
                    onClick={() => setActiveLearningSection((p) => p + 1)}
                    className="text-sm text-slate-600"
                  >
                    Next
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* SOPs & Templates Tab */}
        <TabsContent value="sops" className="mt-6">
          {sops.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
              <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No templates available for this module.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sops.map((sop, idx) => (
                <div
                  key={idx}
                  data-testid={`sop-template-${idx}`}
                  className="bg-white border border-slate-200 rounded-sm shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]"
                >
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                        <FileText className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-body text-sm font-semibold text-slate-900">
                          {sop.title}
                        </h3>
                        <span className="text-xs text-slate-400 uppercase tracking-wider">
                          {sop.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* PDF Download */}
                      <button
                        data-testid={`download-pdf-${idx}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-sm hover:bg-red-700 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          const token = localStorage.getItem("axentia_token");
                          fetch(templateAPI.downloadPdfUrl(modId, idx), {
                            headers: { Authorization: `Bearer ${token}` },
                          })
                            .then((r) => r.blob())
                            .then((blob) => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${sop.title.replace(/ /g, "_")}.pdf`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            });
                        }}
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </button>
                      {/* DOCX Download */}
                      <button
                        data-testid={`download-docx-${idx}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-sm hover:bg-blue-700 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          const token = localStorage.getItem("axentia_token");
                          fetch(templateAPI.downloadDocxUrl(modId, idx), {
                            headers: { Authorization: `Bearer ${token}` },
                          })
                            .then((r) => r.blob())
                            .then((blob) => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${sop.title.replace(/ /g, "_")}.docx`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            });
                        }}
                      >
                        <Download className="w-3 h-3" />
                        DOCX
                      </button>
                      {/* Markdown Download */}
                      <button
                        data-testid={`download-template-${idx}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-sm hover:bg-slate-800 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          const token = localStorage.getItem("axentia_token");
                          fetch(templateAPI.downloadUrl(modId, idx), {
                            headers: { Authorization: `Bearer ${token}` },
                          })
                            .then((r) => r.blob())
                            .then((blob) => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${sop.title.replace(/ /g, "_")}.md`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            });
                        }}
                      >
                        <Download className="w-3 h-3" />
                        MD
                      </button>
                    </div>
                  </div>
                  <div className="p-5">
                    <ContentRenderer content={sop.content} />
                  </div>
                </div>
              ))}
              {!progress.sops && (
                <Button
                  data-testid="mark-sops-complete"
                  onClick={() => markSectionComplete("sops")}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm"
                >
                  <Check className="w-3.5 h-3.5 mr-2" />
                  Mark SOPs as Reviewed
                </Button>
              )}
              {progress.sops && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-700 text-sm">
                  <Check className="w-3.5 h-3.5" />
                  SOPs Reviewed
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Checklists Tab */}
        <TabsContent value="checklists" className="mt-6">
          {checklists.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
              <CheckSquare className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No checklists available for this module.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {checklists.map((cl, clIdx) => (
                <div
                  key={clIdx}
                  data-testid={`checklist-${clIdx}`}
                  className="bg-white border border-slate-200 rounded-sm p-5 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]"
                >
                  <h3 className="font-body text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-slate-600" />
                    {cl.title}
                  </h3>
                  <div className="space-y-2.5">
                    {cl.items.map((item, itemIdx) => {
                      const key = `${clIdx}-${itemIdx}`;
                      return (
                        <label
                          key={key}
                          className="flex items-start gap-3 cursor-pointer group"
                        >
                          <Checkbox
                            data-testid={`checklist-item-${clIdx}-${itemIdx}`}
                            checked={checkedItems[key] || false}
                            onCheckedChange={(checked) =>
                              setCheckedItems((prev) => ({ ...prev, [key]: checked }))
                            }
                            className="mt-0.5 rounded-sm"
                          />
                          <span
                            className={`text-sm leading-relaxed transition-colors ${
                              checkedItems[key]
                                ? "text-slate-400 line-through"
                                : "text-slate-700 group-hover:text-slate-900"
                            }`}
                          >
                            {item}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!progress.checklists && (
                <Button
                  data-testid="mark-checklists-complete"
                  onClick={() => markSectionComplete("checklists")}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm"
                >
                  <Check className="w-3.5 h-3.5 mr-2" />
                  Mark Checklists as Reviewed
                </Button>
              )}
              {progress.checklists && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-700 text-sm">
                  <Check className="w-3.5 h-3.5" />
                  Checklists Reviewed
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Assessment Tab */}
        <TabsContent value="assessment" className="mt-6">
          {progress.assessment_passed && !assessmentResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5 mb-6 flex items-center gap-3">
              <Check className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Assessment Passed</p>
                <p className="text-xs text-emerald-600">
                  Score: {progress.assessment_score}% (Pass mark: {assessment.pass_score}%)
                </p>
              </div>
            </div>
          )}

          {assessmentResult && (
            <div
              data-testid="assessment-result"
              className={`rounded-sm p-5 mb-6 ${
                assessmentResult.passed
                  ? "bg-emerald-50 border border-emerald-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-center gap-3">
                {assessmentResult.passed ? (
                  <Check className="w-5 h-5 text-emerald-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
                <div>
                  <p className={`text-sm font-semibold ${assessmentResult.passed ? "text-emerald-800" : "text-red-800"}`}>
                    {assessmentResult.passed ? "Assessment Passed" : "Assessment Not Passed"}
                    {assessmentResult.grading_type === "ai" && (
                      <Badge className="ml-2 bg-[#005d6c] text-white text-[10px]">AI Graded</Badge>
                    )}
                  </p>
                  <p className={`text-xs ${assessmentResult.passed ? "text-emerald-600" : "text-red-600"}`}>
                    Score: {assessmentResult.score}% ({assessmentResult.correct}/{assessmentResult.total} correct) — Pass mark: {assessment.pass_score}%
                  </p>
                </div>
              </div>
              {/* AI Feedback */}
              {assessmentResult.ai_feedback && Object.keys(assessmentResult.ai_feedback).length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">AI Feedback</p>
                  {Object.entries(assessmentResult.ai_feedback).map(([qId, fb]) => {
                    const q = questions.find(q => q.id === qId);
                    return (
                      <div key={qId} className="bg-white rounded-sm p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-900 mb-1">{q?.question?.substring(0, 80)}...</p>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="h-1.5 flex-1 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${fb.score >= 70 ? 'bg-emerald-500' : fb.score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${fb.score}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-600">{fb.score}%</span>
                        </div>
                        <p className="text-xs text-slate-600">{fb.feedback}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="space-y-6">
            {questions.map((q, qIdx) => {
              // Determine if this question was answered correctly (after submission)
              const userAnswer = answers[q.id];
              const correctAnswer = String(q.correct);
              const isSubmitted = assessmentResult !== null;
              const isCorrect = isSubmitted && userAnswer === correctAnswer;
              const isIncorrect = isSubmitted && userAnswer !== undefined && userAnswer !== correctAnswer;
              
              return (
                <div
                  key={q.id}
                  data-testid={`question-${q.id}`}
                  className={`bg-white border rounded-sm p-5 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] ${
                    isCorrect ? 'border-emerald-300 bg-emerald-50/30' : 
                    isIncorrect ? 'border-red-300 bg-red-50/30' : 
                    'border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <span className={`flex items-center justify-center w-6 h-6 rounded-sm text-xs font-semibold ${
                      isCorrect ? 'bg-emerald-100 text-emerald-700' :
                      isIncorrect ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {isCorrect ? <Check className="w-3.5 h-3.5" /> : 
                       isIncorrect ? <X className="w-3.5 h-3.5" /> : 
                       qIdx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 leading-relaxed">
                        {q.question}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {q.type === "mcq" ? "Multiple Choice" : "Short Answer"}
                        </Badge>
                        {isCorrect && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                            Correct
                          </Badge>
                        )}
                        {isIncorrect && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                            Incorrect
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {q.type === "mcq" && (
                    <div className="space-y-2 ml-9">
                      {q.options.map((option, optIdx) => {
                        const isThisCorrect = String(optIdx) === correctAnswer;
                        const isThisSelected = userAnswer === String(optIdx);
                        const showCorrectHighlight = isSubmitted && isThisCorrect;
                        const showIncorrectHighlight = isSubmitted && isThisSelected && !isThisCorrect;
                        
                        return (
                          <label
                            key={optIdx}
                            data-testid={`option-${q.id}-${optIdx}`}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border cursor-pointer transition-all duration-200 ${
                              showCorrectHighlight
                                ? "border-emerald-400 bg-emerald-50"
                                : showIncorrectHighlight
                                ? "border-red-400 bg-red-50"
                                : isThisSelected
                                ? "border-slate-900 bg-slate-50"
                                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                            } ${isSubmitted ? 'cursor-default' : ''}`}
                          >
                            <input
                              type="radio"
                              name={q.id}
                              value={optIdx}
                              checked={isThisSelected}
                              onChange={() =>
                                !isSubmitted && setAnswers((prev) => ({ ...prev, [q.id]: String(optIdx) }))
                              }
                              disabled={isSubmitted}
                              className="w-3.5 h-3.5 accent-slate-900"
                            />
                            <span className={`text-sm flex-1 ${
                              showCorrectHighlight ? 'text-emerald-700 font-medium' :
                              showIncorrectHighlight ? 'text-red-700' :
                              'text-slate-700'
                            }`}>
                              {option}
                            </span>
                            {showCorrectHighlight && (
                              <Check className="w-4 h-4 text-emerald-600" />
                            )}
                            {showIncorrectHighlight && (
                              <X className="w-4 h-4 text-red-500" />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {q.type === "short_answer" && (
                    <div className="ml-9">
                      <textarea
                        data-testid={`answer-${q.id}`}
                        value={answers[q.id] || ""}
                        onChange={(e) =>
                          !isSubmitted && setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                        }
                        disabled={isSubmitted}
                        placeholder="Write your answer here..."
                        rows={4}
                        className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-900 placeholder:text-slate-400 resize-none"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {questions.length > 0 && (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {assessmentResult && !assessmentResult.passed ? (
                <Button
                  data-testid="try-again-assessment"
                  onClick={() => {
                    setAssessmentResult(null);
                    setAnswers({});
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm px-6"
                >
                  Try Again
                </Button>
              ) : !assessmentResult ? (
                <>
                  <Button
                    data-testid="submit-assessment"
                    onClick={() => handleAssessmentSubmit("standard")}
                    disabled={submitting}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-6"
                  >
                    {submitting && gradingMode === "standard" ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Grading...</>
                    ) : (
                      <>Submit Assessment<ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                  <Button
                    data-testid="submit-assessment-ai"
                    onClick={() => { setGradingMode("ai"); handleAssessmentSubmit("ai"); }}
                    disabled={submitting}
                    variant="outline"
                    className="border-[#005d6c] text-[#005d6c] hover:bg-[#005d6c] hover:text-white rounded-sm px-6"
                  >
                    {submitting && gradingMode === "ai" ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />AI Grading...</>
                    ) : (
                      <><Brain className="w-4 h-4 mr-2" />Submit with AI Grading</>
                    )}
                  </Button>
                </>
              ) : null}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Module Navigation */}
      <Separator className="mt-8" />
      <div className="flex items-center justify-between pt-2">
        {hasPrev ? (
          <Button
            variant="ghost"
            onClick={() => navigate(`/modules/${modId - 1}`)}
            data-testid="prev-module"
            className="text-sm text-slate-600"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Module {modId - 1}
          </Button>
        ) : (
          <div />
        )}
        {hasNext ? (
          <Button
            variant="ghost"
            onClick={() => navigate(`/modules/${modId + 1}`)}
            data-testid="next-module"
            className="text-sm text-slate-600"
          >
            Module {modId + 1}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={() => navigate("/certification")}
            data-testid="go-to-certification"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm"
          >
            View Certification
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
